import React from 'react';
import { Checkbox as ShadcnCheckbox } from '@/components/ui/checkbox';
import { Label as ShadcnLabel } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface CheckboxFieldProps {
    label?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    disabled?: boolean;
    error?: string;
    description?: string;
    className?: string;
    id?: string;
}

export const CheckboxField = React.forwardRef<HTMLButtonElement, CheckboxFieldProps>(
    ({ className, label, checked, defaultChecked, onCheckedChange, disabled, error, description, id, ...props }, ref) => {
        // Generate a stable unique ID if not provided and we have a label
        const generatedId = React.useId();
        const checkboxId = id || (label ? generatedId : undefined);

        const checkboxComponent = (
            <ShadcnCheckbox
                ref={ref}
                id={checkboxId}
                checked={checked}
                defaultChecked={defaultChecked}
                onCheckedChange={onCheckedChange}
                disabled={disabled}
                className={cn(
                    // Glue's checkbox styling adjustments
                    'border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary',
                    error && 'border-destructive data-[state=checked]:bg-destructive data-[state=checked]:border-destructive',
                    className,
                )}
                {...props}
            />
        );

        if (label) {
            return (
                <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                        {checkboxComponent}
                        <ShadcnLabel
                            htmlFor={checkboxId}
                            className={cn(
                                'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                                error && 'text-destructive',
                            )}
                        >
                            {label}
                        </ShadcnLabel>
                    </div>
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
            );
        }

        return checkboxComponent;
    },
);

CheckboxField.displayName = 'CheckboxField';
